import { Box, GridItem, VStack } from '@chakra-ui/react';
import { KYC_STATUSES } from '@shinetools/kyc-validation';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import {
  getKycDocErrorsByCategory,
  getKycDocStatusByCategory,
} from 'features/Bento/libs/kycDocuments';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import { AddressModuleData } from '../../machine/model';
import locales from './locales';

interface HostedHomeProps {
  onHostIdClick: () => void;
  onAccomodation: () => void;
  onPoa: () => void;
  onSelfHostedClick: () => void;
  onContinue: () => void;
  moduleData: AddressModuleData;
}

/**
  A page showing the list of documents to provide for hosted users.
 */
const HostedHome = asBentoPage<HostedHomeProps>(
  ({
    Layout,
    moduleData,
    onAccomodation,
    onContinue,
    onHostIdClick,
    onPoa,
    onPrev,
    onSelfHostedClick,
  }) => {
    const list = (
      [
        {
          action: onHostIdClick,
          category: 'ID_HOST',
          description: locales.hostIdDescription,
          title: locales.hostId,
        },
        {
          action: onAccomodation,
          category: 'PROOF_OF_ACCOMMODATION',
          description: locales.proofOfAccommodationDescription,
          title: locales.proofOfAccommodation,
        },
        {
          action: onPoa,
          category: 'ADDRESS_HOST',
          description: locales.proofOfAddressDescription,
          title: locales.proofOfAddress,
        },
      ] as const
    ).map((item) => ({
      ...item,
      errors: getKycDocErrorsByCategory(
        { kycDocuments: moduleData.viewer.companyUser.kycDocuments },
        item.category,
      ),
      status: getKycDocStatusByCategory(
        { kycDocuments: moduleData.viewer.companyUser.kycDocuments },
        item.category,
      ),
    }));

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Box>
          <Header description={locales.description} title={locales.title} />

          <Box>
            <SunshineCard.Group isBoxed={false} marginBottom="space-64">
              {list.map(({ action, description, errors, status, title }) => {
                const renderIcon = () => {
                  if (
                    [KYC_STATUSES.NONE, KYC_STATUSES.MISSING].includes(status)
                  ) {
                    return <SquircleIcon name="file" />;
                  }

                  if (status === KYC_STATUSES.REFUSED) {
                    return <SquircleIcon colorScheme="red" name="cross" />;
                  }

                  return <SquircleIcon colorScheme="green" name="tick" />;
                };

                return (
                  <SunshineCard
                    alignItems="center"
                    isDisabled={status === 'VALIDATED'}
                    key={title}
                    onClick={action}
                    title={title}
                  >
                    <SunshineCard.Slot name="aside">
                      {renderIcon()}
                    </SunshineCard.Slot>

                    <SunshineCard.Content>
                      <SunshineCard.Text>{description}</SunshineCard.Text>

                      {status === KYC_STATUSES.REFUSED && errors.length > 0 ? (
                        <GridItem colSpan={3}>
                          <VStack
                            marginTop="space-16"
                            paddingBottom="space-8"
                            paddingX="space-8"
                            spacing="space-24"
                          >
                            {errors.map((error) => (
                              <Callout key={error} variant="error">
                                <Callout.Content>{error}</Callout.Content>
                              </Callout>
                            ))}
                          </VStack>
                        </GridItem>
                      ) : null}
                    </SunshineCard.Content>

                    <SunshineCard.Slot name="action">
                      <SunshineCard.SunshineIcon name="chevron-right" />
                    </SunshineCard.Slot>
                  </SunshineCard>
                );
              })}
            </SunshineCard.Group>

            <Button
              isDisabled={list.some(
                ({ status }) =>
                  ![KYC_STATUSES.PENDING, KYC_STATUSES.VALIDATED].includes(
                    status,
                  ),
              )}
              isExpanded
              onClick={onContinue}
            >
              {locales.next}
            </Button>

            {list.some(({ status }) => status === KYC_STATUSES.REFUSED) ? (
              <Button
                icon="arrow-right"
                iconPosition="right"
                marginTop="space-32"
                onClick={onSelfHostedClick}
                variant="inline-primary"
              >
                {locales.selfHostedLink}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Layout>
    );
  },
);

export default HostedHome;
